import { type } from 'arktype';

/**
 * La contraseña exige un mínimo de 8 caracteres — regla de negocio fijada por el usuario
 * (ningún PDF de iteración la fija), aplicada por igual en `loginSchema` y en `registerSchema`.
 * No es solo por simetría: toda cuenta se crea a través de `registerSchema` (o de Google OAuth,
 * que no pasa por este campo), así que ninguna cuenta real puede tener hoy una contraseña más
 * corta — y hoy no hay usuarios en producción, así que no hay ninguna cuenta existente a la que
 * este mínimo pudiera dejar fuera. Exigirlo también al entrar falla rápido en el cliente en vez
 * de delegar en el servidor una comprobación que, para una cuenta legítima, nunca puede fallar.
 */
export const loginSchema = type({
	email: 'string.email',
	password: 'string >= 8',
}).onUndeclaredKey('delete');

export type Login = typeof loginSchema.infer;

/**
 * `redirect` es el destino guardado antes de mandar a la persona a `/login` (documento 02a,
 * §7.1: «La aplicación la manda a `/login?redirect=/unirse/8f3a…`»). Viaja como texto sin
 * más: quien lo consume lo pasa por `safeRedirect` antes de usarlo, la validación de que
 * sea una ruta interna no es responsabilidad del contrato de entrada.
 */
export const registerSchema = type({
	email: 'string.email',
	// Mismo mínimo de 8 caracteres que `loginSchema` — ver el JSDoc de ese schema para el porqué.
	password: 'string >= 8',
	'redirect?': 'string',
}).onUndeclaredKey('delete');

export type Register = typeof registerSchema.infer;

export const recoverSchema = type({
	email: 'string.email',
}).onUndeclaredKey('delete');

export type Recover = typeof recoverSchema.infer;

/**
 * `tokenHash` es el del enlace del correo de recuperación (el que dispara `recoverSchema`);
 * se canjea junto con la contraseña nueva en `POST /api/auth/reset`. Mismo mínimo de 8
 * caracteres que en `loginSchema`/`registerSchema` — ver el JSDoc de `loginSchema` para el
 * porqué.
 */
export const resetPasswordSchema = type({
	password: 'string >= 8',
	tokenHash: 'string >= 1',
}).onUndeclaredKey('delete');

export type ResetPassword = typeof resetPasswordSchema.infer;

/**
 * Respuesta mínima tras login/registro: el JWT nunca viaja en el cuerpo, va en la cookie
 * httpOnly SameSite=Lax que emite el backend (ADR-0005); el cuerpo solo confirma la
 * identidad de la sesión creada.
 */
export const sessionResponseSchema = type({
	email: 'string.email',
	id: 'string.uuid',
}).onUndeclaredKey('delete');

export type SessionResponse = typeof sessionResponseSchema.infer;

/**
 * `POST /api/auth/register` no siempre crea sesión: con la confirmación por correo activada
 * (Supabase Auth), el alta se queda con la sesión pendiente de confirmar hasta que la persona
 * pulse el enlace del correo (documento 02b, §10.1: «Alta pendiente de confirmar → "Te hemos
 * mandado un correo". El destino de retorno viaja en ese correo»). `estado` es un
 * discriminante explícito entre los dos desenlaces, no un campo opcional: no hay `id` de
 * sesión que ofrecer todavía mientras la confirmación sigue pendiente, así que cada rama solo
 * declara lo que existe en su propio caso.
 */
const registerSessionCreatedResponseSchema = type({
	email: 'string.email',
	estado: "'sesionCreada'",
	id: 'string.uuid',
}).onUndeclaredKey('delete');

const registerConfirmationPendingResponseSchema = type({
	email: 'string.email',
	estado: "'confirmacionPendiente'",
}).onUndeclaredKey('delete');

export const registerResponseSchema = registerSessionCreatedResponseSchema.or(
	registerConfirmationPendingResponseSchema,
);

export type RegisterResponse = typeof registerResponseSchema.infer;
